import { HttpClientModule } from '@angular/common/http';
import { ApplicationRef, DoBootstrap, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NotifyService } from '@services/notify.service';
import { SocketService } from '@services/socket.service';
import { Ng2ImgMaxModule } from 'ng2-img-max'; // <-- import the module
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';
import { filter, take } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { AppService } from 'src/app/shared/services/app.service';
import { DebugService } from 'src/app/shared/services/debug.service';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NewitemDialogComponent } from './shared/components/new-item-dialog/new-item-dialog.component';
import { QuantityScanDialogComponent } from './shared/components/quantity_scan/quantity-scan.component';
import { ScannerConfirmDialogComponent } from './shared/components/scanner-confirm-dialog/scanner-confirm-dialog.component';
import { SpokeGuard } from './shared/guards/spoke.guard';
import { SystemAdministratorGuard } from './shared/guards/system-administrator.guard';
import { TableAdministratorGuard } from './shared/guards/table-administrator.guard';
import { UserAdministratorGuard } from './shared/guards/user-administrator.guard';
import { UserGuard } from './shared/guards/user.guard';
import { SharedModule } from './shared/shared.module';

const dbConfig: DBConfig = {
  name: 'posDB',
  version: 1,
  objectStoresMeta: [
    {
      store: 'favTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'posTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'giftTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'inventoryTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'pdfHTMLTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'posTransactionTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'returnInventoryTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    },
    {
      store: 'returnPosTransactionTable',
      storeConfig: { keyPath: 'pk', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        {
          name: 'details',
          keypath: 'details',
          options: { unique: false }
        }
      ]
    }
  ]
};

@NgModule({
  declarations: [
    AppComponent,
    NewitemDialogComponent,
    ScannerConfirmDialogComponent
  ],
  imports: [
    BrowserAnimationsModule, // do not redeclare this in any submodule!
    BrowserModule, // do not redeclare this in any submodule!
    HttpClientModule, //  do not redeclare this in any submodule! // would get loaded if here if you need to make external calls
    SharedModule,
    NgxIndexedDBModule.forRoot(dbConfig),

    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    Ng2ImgMaxModule,
    AppRoutingModule
  ],
  entryComponents: [
    NewitemDialogComponent,
    ScannerConfirmDialogComponent,
    QuantityScanDialogComponent,
    AppComponent
  ],
  providers: [
    // global singleton instances of services should be provided here
    // if you declare them in your own module, and need access elsewhere,
    // they will each have their own instance and will not talk to one another
    ApiService,
    AppService,
    DebugService,
    NotifyService,

    // Guards
    SystemAdministratorGuard,
    TableAdministratorGuard,
    UserAdministratorGuard,
    UserGuard,
    SpokeGuard, // ensure you include {spoke: string} in route.data
    SocketService,
    Ng2ImgMaxModule
  ]
})
export class AppModule implements DoBootstrap {
  isAllowed = false;
  constructor(private _app: AppService, debug: DebugService) {
    debug.__bootstrap();

    console.warn(
      'deprecation notice\n\nthe socket.service has been removed, as the feature is now being handled by auxilium-connect api version.  use `apiService.socket` instead!\n\n'
    );
    console.warn(
      'ALERT *************************\n\nplease ensure you disconnect the old repository `rm -rf ./.git` before committing to\n\n'
    );
  }

  ngDoBootstrap(app: ApplicationRef) {
    const api = environment.api;
    if (api && api.autoAuthenticate) {
      this._app.auth
        .pipe(
          filter((a) => !!a),
          take(1)
        )
        .subscribe(() => {
          app.bootstrap(AppComponent);
        });
    } else {
      app.bootstrap(AppComponent);
    }
  }
}
